/**
 * Topbar actions slot (Roomgrid pattern).
 *
 * Each page projects its primary CTAs ("New account", "Export CSV"…) into
 * the 56px topbar instead of rendering them inside the content area:
 *
 *   <TopbarActions>
 *     <Button variant="contained">New account</Button>
 *   </TopbarActions>
 *
 * The AppBar registers the slot element via `TopbarActionsTarget`; pages
 * portal their children into it. Unmounting the page clears the slot.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Box } from "@mui/material";

interface TopbarActionsContextValue {
  slot: HTMLElement | null;
  registerSlot: (el: HTMLElement | null) => void;
}

const TopbarActionsContext = createContext<TopbarActionsContextValue>({
  slot: null,
  registerSlot: () => undefined,
});

export const TopbarActionsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const registerSlot = useCallback((el: HTMLElement | null) => setSlot(el), []);
  return (
    <TopbarActionsContext.Provider value={{ slot, registerSlot }}>
      {children}
    </TopbarActionsContext.Provider>
  );
};

/** Rendered once by the AppBar — the physical slot the pages portal into. */
export const TopbarActionsTarget: React.FC = () => {
  const { registerSlot } = useContext(TopbarActionsContext);
  return (
    <Box
      ref={registerSlot}
      sx={{ display: "flex", alignItems: "center", gap: 1 }}
    />
  );
};

/** Rendered by pages — teleports its children into the topbar slot. */
export const TopbarActions: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { slot } = useContext(TopbarActionsContext);
  if (!slot) return null;
  return createPortal(children, slot);
};

export default TopbarActions;
